import React, {useEffect, useState} from "react";
import DropZone from "../files/dropzone";
import {
    Button,
    ClickAwayListener,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fade,
    Grid,
    IconButton,
    Link,
    Paper,
    Popper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    LinearProgress,
    Box,
    Chip,
    Tooltip,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import {AddCircleOutline, ArrowBack, Delete, Edit, MoreVert} from "@mui/icons-material";
import {ImportIcon, ExportIcon} from "../../utils/icons";
import { Alert } from '@mui/material';
import {RootGrid, HeaderTypography} from "../catalogmanager/containers/settings/containers/taxonomy";
import { defaultResultsContainerStyles, pageContentGap } from "../../theme";
import EnhancedTable from "../../components/table";
import CatalogManagerResults from "../../components/CatalogManagerResults";
import {utilGetSupplierGroups, utilDeleteSupplierGroup, utilGetPartners, utilCreateSupplierGroup, utilUpdateSupplierGroup} from "../../utils/utils";
import {SupplierGroupDialog} from "./components/SupplierGroupDialog";
import EnhancedConfirmDialog from "../../components/EnhancedConfirmDialog";
import {refreshPartnerData} from "../../helpers";
import EnhancedErrorDialog from "../../components/EnhancedErrorDialog";
import UpdatingDialog from "../../components/Dialogs/UpdatingDialog";
import moment from "moment";
import FileSaver from "filesaver.js-npm";
import Papa from 'papaparse';
import Auth from "../../auth";
const StyledMenu = styled(Paper)({
    background: "#FFFFFF",
    border: "1px solid #DCDCDC",
    borderRadius: "6px",
    zIndex: 1300,
    minWidth: "120px",
    display: "flex",
    flexDirection: "column"
});
const IconContainer = styled('div')({
    display: "flex",
    gap: pageContentGap,
    alignItems: "center",
    justifyContent: "flex-start",
    fontSize: "14px"
});
const MenuButton = styled(Button)({
    justifyContent: "flex-start",
    padding: "6px 16px",
    textTransform: "none",
    fontSize: "14px",
    border: "none",
    "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.04)",
        border: "none"
    }
});
const PreviewDialog = styled(Dialog)({
    "& .MuiDialog-paper": {
        maxWidth: "90vw",
        width: "900px",
        maxHeight: "80vh"
    },
    "& .MuiDialogContent-root": {
        display: "flex",
        flexDirection: "column",
        padding: "16px 24px"
    }
});
const PreviewTable = styled(Table)({
    "& .MuiTableCell-root": {
        fontSize: "0.875rem"
    }
});
const ActionChip = styled(Chip)({
    fontWeight: "bold",
    fontSize: "0.75rem"
});
const OldValue = styled('div')(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    marginBottom: "4px"
}));
const NewValue = styled('div')({
    fontWeight: 500,
    fontSize: "0.85rem"
});
const ProgressContainer = styled('div')({
    padding: "20px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden"
});
const ProgressItem = styled('div')({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: "8px 0",
    padding: "8px 12px",
    backgroundColor: "#fafafa",
    borderRadius: "4px"
});
const SummaryBox = styled(Box)({
    marginBottom: "16px"
});
const BulkActions = (props: any) => {
    const {table, onBulkDelete} = props;
    return (
        <Grid container style={{gap: "0.5em", flex: 1}}>
            <Button 
                variant='outlined' 
                onClick={() => onBulkDelete(table.state.selected)}
                startIcon={<Delete/>}
                style={{
                    color: '#f44336',
                    borderColor: '#f44336'
                }}
            >
                Delete
            </Button>
        </Grid>
    );
};
const iconButtonSx = (theme: any) => ({
    backgroundColor: "white",
    border: "none",
    borderRadius: "6px",
    "& svg": { color: "rgba(0, 0, 0, 0.54)" }
});

interface SupplierGroupsSettingsProps {
    setTitle?: (title: string) => void;
    history: { push: (path: string) => void; goBack: () => void; [key: string]: any };
    location: { search?: string; key?: string; [key: string]: any };
    [key: string]: any;
}

interface ImportAddItem {
    name: string;
    supplierIds: any[];
}

interface ImportUpdateItem {
    id: any;
    name: string;
    supplierIds: any[];
}

interface ImportRemoveItem {
    id: any;
    name: string;
}

interface ImportSkippedItem {
    name: string;
    reason: string;
}

interface ImportPlan {
    errors: string[];
    toAdd: ImportAddItem[];
    toUpdate: ImportUpdateItem[];
    toRemove: ImportRemoveItem[];
    skipped: ImportSkippedItem[];
}

interface ImportPreviewItem {
    name: string;
    currentSuppliers: string[];
    newSuppliers: string[];
    action: 'create' | 'update' | 'remove';
    id?: any;
    supplierIds?: any[];
}

interface ImportResults {
    success: string[];
    errors: string[];
}

export default function SupplierGroupsSettings(props: SupplierGroupsSettingsProps) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [loading, setLoading] = useState(true);
    const [groups, setGroups] = useState<any[]>([]);
    const [partners, setPartners] = useState<any[]>([]);
    const [reload, setReload] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState<any>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deletingGroupId, setDeletingGroupId] = useState<any>(null);
    const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
    const [bulkDeleteGroupIds, setBulkDeleteGroupIds] = useState<any[]>([]);
    const [updatingDialogOpen, setUpdatingDialogOpen] = useState(false);
    const [error, setError] = useState({open: false, errorHeader: "", errorMessage: ""});
    const [importFileInput, setImportFileInput] = useState<HTMLInputElement | null>(null);
    const [anchorEl, setAnchorEl] = useState<any>(null);
    const [selectedGroup, setSelectedGroup] = useState<any>(null);
    const [importPreviewOpen, setImportPreviewOpen] = useState(false);
    const [importData, setImportData] = useState<ImportPlan | null>(null);
    const [importProgress, setImportProgress] = useState(0);
    const [importResults, setImportResults] = useState<ImportResults | null>(null);
    const [importProcessing, setImportProcessing] = useState(false);
    const [importDetailedPreview, setImportDetailedPreview] = useState<ImportPreviewItem[]>([]);
    const [importInstructionsOpen, setImportInstructionsOpen] = useState(false);
    const [config, setConfig] = useState<any>({
        getData: async () => [],
        savedSearchEnabled: true,
        columns: [],
        keyField: "Id",
        order: "asc",
        orderBy: "Name",
        pageSize: 20,
        pageSizes: [10, 20, 50, 100],
        refresh: false,
        title: "Supplier Groups",
        filterInUrl: false,
        filter: null
    });
    const canManage = Auth.hasPermission("settings/manage");
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, group: any) => {
        event.preventDefault();
        event.stopPropagation();
        setSelectedGroup(group);
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedGroup(null);
    };
    const handleEdit = () => {
        if (selectedGroup) {
            setEditingGroup(selectedGroup);
            setDialogOpen(true);
            handleMenuClose();
        }
    };
    const handleDeleteClick = () => {
        if (selectedGroup) {
            setDeletingGroupId(selectedGroup.Id);
            setDeleteConfirmOpen(true);
            handleMenuClose();
        }
    };
    const exportGroups = () => {
        const header = ["Group Name", "Supplier IDs", "Supplier Names"];
        const rows = [header].concat(groups.map(g => [
            g.Name,
            g.PartnerIds ? g.PartnerIds.join(";") : "",
            g.Partners ? g.Partners.join(";") : ""
        ]));
        const output = Papa.unparse(rows);
        const blob = new Blob([output], { type: "text/csv" });
        (FileSaver as any).saveAs(blob, `supplier_groups_${moment().format('YYYY-MM-DD')}.csv`);
    };
    const parseCSVFile = async (file: File): Promise<Record<string, string>[]> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const text = e.target.result;
                    // Ensure text is a string for papaparse
                    if (typeof text !== 'string') {
                        reject(new Error('Failed to read file as text'));
                        return;
                    }
                    const result = Papa.parse(text, {
                        header: true,
                        skipEmptyLines: true,
                        transformHeader: h => h.trim(),
                        transform: v => v.trim()
                    });
                    resolve(result.data);
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = reject;
            reader.readAsText(file);
        });
    };
    const validateAndPrepareImport = async (records: Record<string, string>[]): Promise<ImportPlan> => {
        const errors: string[] = [];
        const toAdd: ImportAddItem[] = [];
        const toUpdate: ImportUpdateItem[] = [];
        const toRemove: ImportRemoveItem[] = [];
        const skipped: ImportSkippedItem[] = [];
        // Get current groups for comparison
        const currentGroups = groups;
        // Check if partners are loaded
        const hasPartners = partners && partners.length > 0;
        if (!hasPartners) {
            console.warn("Partners list is empty or not loaded. Skipping supplier ID validation.");
        }
        // Validate required columns
        if (records.length > 0) {
            const requiredColumns = ['Group Name'];
            const columns = Object.keys(records[0]);
            const missingColumns = requiredColumns.filter(col => !columns.includes(col));
            if (missingColumns.length > 0) {
                errors.push(`Missing required columns: ${missingColumns.join(', ')}`);
                return { errors, toAdd, toUpdate, toRemove, skipped };
            }
        }
        // Process each record
        for (let i = 0; i < records.length; i++) {
            const record = records[i];
            const groupName = record['Group Name'];
            if (!groupName) {
                errors.push(`Row ${i + 2}: Group name is required`);
                continue;
            }
            // Parse supplier IDs and convert to integers
            const supplierIds: any[] = (record['Supplier IDs'] || '')
                .split(';')
                .map(id => id.trim())
                .filter(id => id)
                .map(id => {
                    const parsed = parseInt(id);
                    return isNaN(parsed) ? id : parsed;  // Keep as string if not a valid number
                });
            // Find existing group
            const existingGroup = currentGroups.find(g => g.Name === groupName);
            // Auto-detect action based on group existence and supplier IDs
            if (existingGroup) {
                // Group exists
                if (supplierIds.length === 0) {
                    // Empty supplier list = DELETE
                    toRemove.push({
                        id: existingGroup.Id,
                        name: groupName
                    });
                } else {
                    // Check if suppliers changed (convert both to numbers for comparison)
                    // Use PartnerIds field which contains the raw IDs
                    const existingPartnerIds = (existingGroup.PartnerIds || []).map(id => parseInt(id)).filter(id => !isNaN(id)).sort((a, b) => a - b);
                    const newPartnerIds = supplierIds.map(id => parseInt(id)).filter(id => !isNaN(id)).sort((a, b) => a - b);
                    if (JSON.stringify(existingPartnerIds) !== JSON.stringify(newPartnerIds)) {
                        // Suppliers changed = UPDATE
                        // Only validate supplier IDs if partners are loaded
                        if (hasPartners) {
                            const invalidIds: any[] = [];
                            for (const id of supplierIds) {
                                const found = partners.find(p => 
                                    p.Id === id || 
                                    p.Id === parseInt(id) || 
                                    String(p.Id) === String(id)
                                );
                                if (!found) {
                                    invalidIds.push(id);
                                }
                            }
                            if (invalidIds.length > 0) {
                                // Still allow the update but note the invalid IDs as a warning
                                console.warn(`Row ${i + 2}: Some supplier IDs not found in partners list: ${invalidIds.join(', ')}`);
                                // The API will validate them server-side
                            }
                        }
                        toUpdate.push({
                            id: existingGroup.Id,
                            name: groupName,
                            supplierIds
                        });
                    } else {
                        // No changes = SKIP
                        skipped.push({
                            name: groupName,
                            reason: 'No changes detected'
                        });
                    }
                }
            } else {
                // Group doesn't exist
                if (supplierIds.length > 0) {
                    // Has suppliers = CREATE
                    // Only validate supplier IDs if partners are loaded
                    if (hasPartners) {
                        const invalidIds: any[] = [];
                        for (const id of supplierIds) {
                            const found = partners.find(p => 
                                p.Id === id || 
                                p.Id === parseInt(id) || 
                                String(p.Id) === String(id)
                            );
                            if (!found) {
                                invalidIds.push(id);
                            }
                        }
                        if (invalidIds.length > 0) {
                            // Still add the group but note the invalid IDs as a warning
                            console.warn(`Row ${i + 2}: Some supplier IDs not found in partners list: ${invalidIds.join(', ')}`);
                            // For now, we'll still allow the import with whatever IDs were provided
                            // The API will validate them server-side
                        }
                    }
                    toAdd.push({
                        name: groupName,
                        supplierIds
                    });
                } else {
                    // No suppliers for new group = SKIP
                    skipped.push({
                        name: groupName,
                        reason: 'Cannot create group without suppliers'
                    });
                }
            }
        }
        return { errors, toAdd, toUpdate, toRemove, skipped };
    };
    const processImport = async (): Promise<void> => {
        if (!importData) return;
        setImportProgress(0);
        const results: ImportResults = {
            success: [],
            errors: []
        };
        const totalOperations = 
            importData.toAdd.length + 
            importData.toUpdate.length + 
            importData.toRemove.length;
        let completed = 0;
        // Process removals first
        for (const group of importData.toRemove) {
            try {
                let result = await utilDeleteSupplierGroup(group.id);
                if (result.errorMessage){
                    results.errors.push(`Failed to delete ${group.name}: ${result.errorMessage}`);
                } else {
                    results.success.push(`Deleted group: ${group.name}`);
                }
            } catch (error: any) {
                results.errors.push(`Failed to delete ${group.name}: ${error.message}`);
            }
            completed++;
            setImportProgress((completed / totalOperations) * 100);
        }
        // Process updates
        for (const group of importData.toUpdate) {
            try {
                let result = await utilUpdateSupplierGroup(group.id, group.name, group.supplierIds);
                if (result.errorMessage){
                    results.errors.push(`Failed to update ${group.name}: ${result.errorMessage}`);
                } else {
                    results.success.push(`Updated group: ${group.name}`);
                }
            } catch (error: any) {
                results.errors.push(`Failed to update ${group.name}: ${error.message}`);
            }
            completed++;
            setImportProgress((completed / totalOperations) * 100);
        }
        // Process additions
        for (const group of importData.toAdd) {
            try {
                let result = await utilCreateSupplierGroup(group.name, group.supplierIds);
                if (result.errorMessage){
                    results.errors.push(`Failed to create ${group.name}: ${result.errorMessage}`);
                } else {
                    results.success.push(`Created group: ${group.name}`);
                }
            } catch (error: any) {
                results.errors.push(`Failed to create ${group.name}: ${error.message}`);
            }
            completed++;
            setImportProgress((completed / totalOperations) * 100);
        }
        setImportResults(results);
        setImportData(null);
        // Refresh the table
        setReload(!reload);
        await refreshPartnerData(true);
    };
    const importGroups = async (event: any): Promise<void> => {
        const file = event.target.files[0];
        if (!file) return;
        // Reset previous import results when starting a new import
        setImportResults(null);
        setImportProcessing(false);
        try {
            const records = await parseCSVFile(file);
            const importData = await validateAndPrepareImport(records);
            if (importData.errors.length > 0) {
                setError({
                    open: true,
                    errorHeader: "Import Validation Errors",
                    errorMessage: importData.errors.join('\n')
                });
            } else {
                // Create detailed preview data
                const detailedPreview: ImportPreviewItem[] = [];
                // Ensure partners are loaded for name resolution
                let partnersForLookup = partners;
                if (!partnersForLookup || partnersForLookup.length === 0) {
                    try {
                        const partnersData = await utilGetPartners();
                        // Only treat an actual array as partners; on failure utilGetPartners resolves to
                        // {open, errorHeader, errorMessage} (errorMessage may be undefined), which must not
                        // be stored as partner data (it would break the later partners.find lookups).
                        if (Array.isArray(partnersData)) {
                            partnersForLookup = partnersData;
                            setPartners(partnersData);
                        }
                    } catch (err) {
                        console.error("Failed to load partners for import preview:", err);
                    }
                }
                // Add items to be created
                importData.toAdd.forEach(item => {
                    const supplierNames = item.supplierIds.map(id => {
                        // Handle both string and number comparisons
                        const partner = partnersForLookup.find(p => 
                            p.Id === id || 
                            p.Id === parseInt(id) || 
                            String(p.Id) === String(id)
                        );
                        return partner ? partner.CompanyName : `Unknown Supplier (${id})`;
                    });
                    detailedPreview.push({
                        name: item.name,
                        currentSuppliers: [],
                        newSuppliers: supplierNames,
                        action: 'create',
                        supplierIds: item.supplierIds
                    });
                });
                // Add items to be updated
                importData.toUpdate.forEach(item => {
                    const existingGroup = groups.find(g => g.Id === item.id);
                    const currentSuppliers = existingGroup?.Partners || [];
                    const newSuppliers = item.supplierIds.map(id => {
                        // Handle both string and number comparisons
                        const partner = partnersForLookup.find(p => 
                            p.Id === id || 
                            p.Id === parseInt(id) || 
                            String(p.Id) === String(id)
                        );
                        return partner ? partner.CompanyName : `Unknown Supplier (${id})`;
                    });
                    // Only add to preview if there are actual changes
                    // Compare sorted arrays to detect differences
                    const currentIds = (existingGroup?.PartnerIds || []).map(id => String(id)).sort();
                    const newIds = item.supplierIds.map(id => String(id)).sort();
                    const hasChanges = JSON.stringify(currentIds) !== JSON.stringify(newIds);
                    if (hasChanges) {
                        detailedPreview.push({
                            name: item.name,
                            currentSuppliers,
                            newSuppliers,
                            action: 'update',
                            id: item.id,
                            supplierIds: item.supplierIds
                        });
                    }
                });
                // Add items to be removed
                importData.toRemove.forEach(item => {
                    const existingGroup = groups.find(g => g.Id === item.id);
                    const currentSuppliers = (existingGroup?.Partners || []).map(p => p.CompanyName || p.Id);
                    detailedPreview.push({
                        name: item.name,
                        currentSuppliers,
                        newSuppliers: [],
                        action: 'remove',
                        id: item.id
                    });
                });
                setImportData(importData);
                setImportDetailedPreview(detailedPreview);
                setImportPreviewOpen(true);
            }
        } catch (error: any) {
            setError({
                open: true,
                errorHeader: "Import Error",
                errorMessage: `Failed to parse CSV file: ${error.message}`
            });
        }
        // Reset file input safely
        if (event && event.target) {
            event.target.value = null;
        }
    };
    const handleDelete = async (): Promise<void> => {
        try {
            const result = await utilDeleteSupplierGroup(deletingGroupId);
            if (result.errorMessage) {
                setError({
                    open: true,
                    errorHeader: "Delete Error",
                    errorMessage: result.errorMessage
                });
            } else {
                setReload(!reload);
                await refreshPartnerData(true);
            }
        } catch (err: any) {
            setError({
                open: true,
                errorHeader: "Delete Error",
                errorMessage: err.message
            });
        }
        setDeleteConfirmOpen(false);
        setDeletingGroupId(null);
    };
    const handleBulkDelete = async (): Promise<void> => {
        setUpdatingDialogOpen(true);
        setBulkDeleteConfirmOpen(false);
        const errors: string[] = [];
        for (const groupId of bulkDeleteGroupIds) {
            try {
                const result = await utilDeleteSupplierGroup(groupId);
                if (result.errorMessage) {
                    const group = groups.find(g => g.Id === groupId);
                    errors.push(`Failed to delete "${group?.Name || groupId}": ${result.errorMessage}`);
                }
            } catch (err: any) {
                const group = groups.find(g => g.Id === groupId);
                errors.push(`Failed to delete "${group?.Name || groupId}": ${err.message}`);
            }
        }
        setUpdatingDialogOpen(false);
        if (errors.length > 0) {
            setError({
                open: true,
                errorHeader: "Delete Errors",
                errorMessage: errors.join('\n')
            });
        }
        setBulkDeleteGroupIds([]);
        setReload(!reload);
        await refreshPartnerData(true);
    };
    // Fetch partners once when component mounts
    useEffect(() => {
        const fetchPartners = async () => {
            try {
                const partnersData = await utilGetPartners();
                // Array on success; {open, errorHeader, errorMessage} on failure (errorMessage may be
                // undefined). Guard on Array.isArray so a failure object is never stored as partner data.
                if (Array.isArray(partnersData)) {
                    setPartners(partnersData);
                }
            } catch (err) {
                console.error("Error fetching partners:", err);
            }
        };
        fetchPartners();
    }, []);
    useEffect(() => {
        setConfig({
            getData: async () => {
                try {
                    // Fetch supplier groups
                    let groupsData = await utilGetSupplierGroups();
                    if (groupsData.errorMessage) {
                        setError({
                            open: true,
                            errorHeader: "Error loading groups",
                            errorMessage: groupsData.errorMessage
                        });
                        return [];
                    }
                    // Ensure groupsData is an array
                    if (!Array.isArray(groupsData)) {
                        console.error("Supplier groups data is not an array:", groupsData);
                        return [];
                    }
                    // Process groups: add Id field, preserve PartnerIds, and map to partner names
                    groupsData = groupsData.map((group, index) => ({
                        ...group,
                        Id: group.Id || group.Name, // Use Name as Id if Id doesn't exist
                        // Preserve original partner IDs for import/export
                        PartnerIds: group.Partners ? group.Partners.map(p => {
                            if (typeof p === 'string' || typeof p === 'number') return p;
                            return p.Id || p;
                        }) : [],
                        // Map to partner names for display and filtering
                        Partners: group.Partners ? group.Partners.map(p => {
                            if (typeof p === 'string') {
                                const partner = partners.find(partner => partner.CompanyName === p);
                                return partner ? partner.CompanyName : p;
                            }
                            if (p.CompanyName) return p.CompanyName;
                            const partner = partners.find(partner => partner.Id === (p.Id || p));
                            return partner ? partner.CompanyName : `Unknown (${p.Id || p})`;
                        }) : []
                    }));
                    setGroups(groupsData);
                    setLoading(false);
                    return groupsData;
                } catch (err: any) {
                    setError({
                        open: true,
                        errorHeader: "Error loading data",
                        errorMessage: err.message
                    });
                    setLoading(false);
                    return [];
                }
            },
        columns: [
            {
                id: "Name",
                label: "Group Name",
                searchable: true,
                sortable: true,
                width: "15em"
            },
            {
                id: "Partners",
                label: "Suppliers",
                searchable: false,
                sortable: false,
                filterable: true,
                type: "array",
                getOptions: async () => {
                    let partnersData = await utilGetPartners();
                    // Coerce any non-array (the {open, errorHeader, errorMessage} failure object, whose
                    // errorMessage may be undefined) to [] so the .map below can't crash.
                    if (!Array.isArray(partnersData)) partnersData = [];
                    return partnersData.map(p => ({
                        label: p.CompanyName,
                        value: p.CompanyName
                    }));
                },
                template: (row) => {
                    const partners = row.Partners || [];
                    if (partners.length === 0) return <Typography color="textSecondary">No suppliers</Typography>;
                    const displayPartners = partners.slice(0, 5);
                    const remaining = partners.length - 5;
                    return (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
                            {displayPartners.map((partner, index) => (
                                <Tooltip
                                    key={index}
                                    title={partner}
                                    placement="top"
                                >
                                    <Chip
                                        size="small"
                                        label={partner}
                                        variant="outlined"
                                        style={{
                                            maxWidth: "150px",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis"
                                        }}
                                    />
                                </Tooltip>
                            ))}
                            {remaining > 0 && (
                                <Typography variant="caption" style={{fontStyle: "italic", color: "#666"}}>
                                    +{remaining} more
                                </Typography>
                            )}
                        </div>
                    );
                }
            },
            {
                id: "actions",
                label: "Actions",
                filterable: false,
                sortable: false,
                width: "5em",
                template: (row) => (
                    <IconButton
                        size="small"
                        onClick={(event) => handleMenuOpen(event, row)}
                        disabled={!canManage}
                        style={{borderRadius: 100}}
                    >
                        <MoreVert/>
                    </IconButton>
                )
            }
        ],
        keyField: "Id",
        order: "asc",
        orderBy: "Name",
        pageSize: 20,
        pageSizes: [10, 20, 50, 100],
        refresh: reload,
        title: "Supplier Groups",
        filterInUrl: false,
        filter: null
    });
    }, [reload, canManage, partners]);
    useEffect(() => {
        if (props.setTitle) {
            props.setTitle('PMC: Supplier Groups');
        }
    }, [props]);
    return (
        <RootGrid container style={{gap: pageContentGap, padding: "24px 14px 24px 14px", position: "relative"}}>
            <Grid container justifyContent={"space-between"} direction={"row"}>
                <Grid item container justifyContent={"flex-start"} alignItems={"center"} style={{gap: pageContentGap, width: "100%"}}>
                    <IconButton
                        sx={iconButtonSx}
                        style={{height: "36px", width: "36px", position: "relative", zIndex: 2}}
                        onClick={() => props.history.goBack()}
                        size="large">
                        <ArrowBack style={{height: "18px", width: "18px"}}/>
                    </IconButton>
                    <HeaderTypography>
                        Supplier Groups
                    </HeaderTypography>
                    <Grid item style={{marginLeft: "auto", display: "flex", gap: "8px"}}>
                    {canManage && (
                        <>
                            <input
                                type="file"
                                accept=".csv"
                                style={{display: "none"}}
                                ref={(ref) => setImportFileInput(ref)}
                                onChange={importGroups}
                            />
                            <Button
                                variant="outlined"
                                startIcon={<ImportIcon style={{height: "18px", width: "18px"}}/>}
                                onClick={() => setImportInstructionsOpen(true)}
                            >
                                Import
                            </Button>
                        </>
                    )}
                    <Button
                        variant="outlined"
                        startIcon={<ExportIcon style={{height: "18px", width: "18px"}}/>}
                        onClick={exportGroups}
                        disabled={groups.length === 0}
                    >
                        Export
                    </Button>
                    {canManage && (
                        <Button
                            variant='outlined'
                            startIcon={<AddCircleOutline style={{height: "18px", width: "18px"}}/>}
                            onClick={() => {
                                setEditingGroup(null);
                                setDialogOpen(true);
                            }}
                        >
                            Add Group
                        </Button>
                    )}
                    </Grid>
                </Grid>
            </Grid>
            <Grid size={12}>
                <Paper style={{position: "relative", zIndex: 0}}>
                    <Alert severity="info" style={{marginBottom: "1em", padding: "1em", position: "relative"}}>
                        <Typography variant="body2">
                            Groups can be seen and managed by everyone on your team. Users with settings/manage permissions can create, edit and delete Supplier Groups
                        </Typography>
                    </Alert>
                </Paper>
                <EnhancedTable 
                        config={config} 
                        history={props.history} 
                        location={props.location}
                        key="supplier_groups_table"
                        template={(table) =>
                            <CatalogManagerResults
                            disableColumnSelector={true}
                            isSelectable={true}
                            disableTermInUrl={true}
                            condensed={false}
                            noToolBar={false}
                            history={props.history} 
                            location={props.location}
                            table={table}
                            config={config}
                            setConfig={setConfig}
                            resultsContainerStyles={defaultResultsContainerStyles}
                            renderButtonFiltersAfter={false}
                            showSavedSearch={true}
                            savedSearchTableType="supplier-groups"
                            bulkActions={canManage && table.state.selected && table.state.selected.length > 0 && 
                                <BulkActions 
                                    table={table} 
                                    onBulkDelete={(selectedIds) => {
                                        setBulkDeleteGroupIds(selectedIds);
                                        setBulkDeleteConfirmOpen(true);
                                    }}
                                />
                            }
                        />
                        }
                    />
            </Grid>
            <SupplierGroupDialog
                open={dialogOpen}
                setOpen={setDialogOpen}
                group={editingGroup}
                partners={partners}
                onSave={async () => {
                    setReload(!reload);
                    await refreshPartnerData(true);
                }}
            />
            <EnhancedConfirmDialog
                open={deleteConfirmOpen}
                setOpen={setDeleteConfirmOpen}
                title="Delete Supplier Group"
                message="Are you sure you want to delete this supplier group? This action cannot be undone."
                type="delete"
                action={(confirmed) => {
                    if (confirmed) {
                        handleDelete();
                    } else {
                        setDeletingGroupId(null);
                    }
                }}
            />
            <EnhancedConfirmDialog
                open={bulkDeleteConfirmOpen}
                setOpen={setBulkDeleteConfirmOpen}
                title="Delete Supplier Groups"
                message={`Are you sure you want to delete ${bulkDeleteGroupIds.length} supplier group${bulkDeleteGroupIds.length > 1 ? 's' : ''}? This action cannot be undone.`}
                type="delete"
                action={(confirmed) => {
                    if (confirmed) {
                        handleBulkDelete();
                    } else {
                        setBulkDeleteGroupIds([]);
                    }
                }}
            />
            <UpdatingDialog
                open={updatingDialogOpen}
                setOpen={setUpdatingDialogOpen}
                title="Deleting Supplier Groups..."
                subtitle={`Deleting ${bulkDeleteGroupIds.length} supplier group${bulkDeleteGroupIds.length > 1 ? 's' : ''}...`}
            />
            <EnhancedErrorDialog
                open={error.open}
                errorHeader={error.errorHeader}
                errorMessage={error.errorMessage}
                onClose={() => setError({open: false, errorHeader: "", errorMessage: ""})}
            />
            <Popper 
                open={Boolean(anchorEl)} 
                anchorEl={anchorEl} 
                transition 
                placement="left-start"
                style={{zIndex: 1300}}
            >
                {({TransitionProps}) => (
                    <ClickAwayListener onClickAway={handleMenuClose}>
                        <Fade {...TransitionProps} timeout={350}>
                            <StyledMenu>
                                <MenuButton
                                    onClick={handleEdit}
                                >
                                    <IconContainer>
                                        <Edit style={{width: "20px", height: "20px"}}/>
                                        <Typography variant="body2">Edit</Typography>
                                    </IconContainer>
                                </MenuButton>
                                <MenuButton
                                    onClick={handleDeleteClick}
                                    style={{color: "#f44336"}}
                                >
                                    <IconContainer>
                                        <Delete style={{width: "20px", height: "20px", color: "#f44336"}}/>
                                        <Typography variant="body2" style={{color: "#f44336"}}>Delete</Typography>
                                    </IconContainer>
                                </MenuButton>
                            </StyledMenu>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
            {/* Enhanced Import Preview Dialog */}
            <PreviewDialog
                open={importPreviewOpen}
                onClose={() => {
                    if (!importProcessing) {
                        setImportPreviewOpen(false);
                        setImportResults(null);
                        setImportData(null);
                        setImportDetailedPreview([]);
                        if (importFileInput) {
                            importFileInput.value = null;
                        }
                    }
                }}
                maxWidth={false}
                fullWidth={false}
            >
                <DialogTitle>
                    {importResults ? "Import Complete" : importProcessing ? "Processing Import" : "Import Preview"}
                </DialogTitle>
                <DialogContent dividers>
                    {importResults ? (
                        /* Results Section After Import */
                        (<ProgressContainer style={{ minHeight: 0 }}>
                            <Typography variant="h6" gutterBottom>
                                Import Complete
                            </Typography>
                            <SummaryBox>
                                <Typography variant="body1" style={{ marginBottom: "8px" }}>
                                    <strong>Summary:</strong> {importResults.success.length} successful, {importResults.errors.length} failed
                                </Typography>
                                {importResults.errors.length > 0 && (
                                    <Alert severity="warning" style={{ marginBottom: "8px" }}>
                                        {importResults.errors.length} operation(s) failed. Check the details below.
                                    </Alert>
                                )}
                                {importResults.success.length > 0 && importResults.errors.length === 0 && (
                                    <Alert severity="success">
                                        All operations completed successfully!
                                    </Alert>
                                )}
                            </SummaryBox>
                            <Box style={{ flexGrow: 1, overflow: "auto", marginTop: "16px", textAlign: "left" }}>
                                {/* Show successful operations */}
                                {importResults.success.map((msg, index) => {
                                    const [action, ...rest] = msg.split(':');
                                    const groupName = rest.join(':').trim();
                                    return (
                                        <ProgressItem key={`success-${index}`}>
                                            <Typography variant="body2">{groupName || msg}</Typography>
                                            <Typography color="primary">✓ {action || 'Completed'}</Typography>
                                        </ProgressItem>
                                    );
                                })}
                                {/* Show failed operations */}
                                {importResults.errors.map((msg, index) => {
                                    const [failedPart, ...errorParts] = msg.split(':');
                                    const errorMessage = errorParts.join(':').trim();
                                    return (
                                        <ProgressItem key={`error-${index}`} style={{ backgroundColor: "#ffebee" }}>
                                            <Typography variant="body2">{failedPart}</Typography>
                                            <Typography color="error" style={{ fontSize: "0.875rem" }}>✗ {errorMessage || 'Failed'}</Typography>
                                        </ProgressItem>
                                    );
                                })}
                            </Box>
                        </ProgressContainer>)
                    ) : !importProcessing ? (
                        <>
                            {/* Summary Section */}
                            {importData && (
                                <Box>
                                    {/* Check if there are any changes */}
                                    {(importData.toAdd?.length > 0 || importData.toUpdate?.length > 0 || importData.toRemove?.length > 0) ? (
                                        <>
                                            <Typography variant="body1" gutterBottom>
                                                The following changes will be applied based on your CSV file:
                                            </Typography>
                                            <Grid container spacing={2} style={{marginTop: "8px", marginBottom: "16px"}}>
                                                {importData.toAdd && importData.toAdd.length > 0 && (
                                                    <Grid item>
                                                        <ActionChip
                                                            label={`${importData.toAdd.length} to create`}
                                                            style={{backgroundColor: "#e8f5e9", color: "#2e7d32"}}
                                                        />
                                                    </Grid>
                                                )}
                                                {importData.toUpdate && importData.toUpdate.length > 0 && (
                                                    <Grid item>
                                                        <ActionChip
                                                            label={`${importData.toUpdate.length} to update`}
                                                            style={{backgroundColor: "#e3f2fd", color: "#1565c0"}}
                                                        />
                                                    </Grid>
                                                )}
                                                {importData.toRemove && importData.toRemove.length > 0 && (
                                                    <Grid item>
                                                        <ActionChip
                                                            label={`${importData.toRemove.length} to delete`}
                                                            style={{backgroundColor: "#ffebee", color: "#c62828"}}
                                                        />
                                                    </Grid>
                                                )}
                                                {importData.skipped && importData.skipped.length > 0 && (
                                                    <Grid item>
                                                        <ActionChip
                                                            label={`${importData.skipped.length} to skip`}
                                                            style={{backgroundColor: "#f5f5f5", color: "#616161"}}
                                                        />
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </>
                                    ) : (
                                        <Alert severity="info" style={{marginBottom: 0}}>
                                            <Typography variant="body2">
                                                No changes to apply. All supplier groups in the imported file match the current configuration.
                                                {importData.skipped && importData.skipped.length > 0 && 
                                                    ` (${importData.skipped.length} group(s) were skipped)`
                                                }
                                            </Typography>
                                        </Alert>
                                    )}
                                </Box>
                            )}
                            {/* Detailed Preview Table */}
                            {Array.isArray(importDetailedPreview) && importDetailedPreview.length > 0 ? (
                                <TableContainer component={Paper} style={{maxHeight: "300px", marginTop: "16px"}}>
                                    <PreviewTable stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Group Name</TableCell>
                                                <TableCell>Current Suppliers</TableCell>
                                                <TableCell>New Suppliers</TableCell>
                                                <TableCell align="center">Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {importDetailedPreview.map((preview, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <Typography variant="body2" style={{fontWeight: 500}}>
                                                            {preview.name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {preview.currentSuppliers && preview.currentSuppliers.length > 0 ? (
                                                            <OldValue>
                                                                {preview.currentSuppliers.join(", ")}
                                                            </OldValue>
                                                        ) : (
                                                            <Typography variant="body2" color="textSecondary">
                                                                (new group)
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {preview.newSuppliers && preview.newSuppliers.length > 0 ? (
                                                            <NewValue>
                                                                {preview.newSuppliers.join(", ")}
                                                            </NewValue>
                                                        ) : (
                                                            <Typography variant="body2" color="textSecondary">
                                                                (empty - will be deleted)
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            size="small"
                                                            label={preview.action}
                                                            style={{
                                                                backgroundColor: 
                                                                    preview.action === 'create' ? "#e8f5e9" :
                                                                    preview.action === 'update' ? "#e3f2fd" :
                                                                    preview.action === 'remove' ? "#ffebee" : "#f5f5f5",
                                                                color: 
                                                                    preview.action === 'create' ? "#2e7d32" :
                                                                    preview.action === 'update' ? "#1565c0" :
                                                                    preview.action === 'remove' ? "#c62828" : "#616161",
                                                                fontWeight: "bold"
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </PreviewTable>
                                </TableContainer>
                            ) : (
                                /* Show message when all items are skipped */
                                (importData && importData.skipped && importData.skipped.length > 0 && 
                                !importData.toAdd?.length && !importData.toUpdate?.length && !importData.toRemove?.length && (<Box mt={2}>
                                    <Typography variant="body1" color="textSecondary" align="center">
                                        All groups in the imported file match the current configuration. No changes needed.
                                    </Typography>
                                </Box>))
                            )}
                        </>
                    ) : (
                        /* Progress Section During Import */
                        (<ProgressContainer style={{ minHeight: 0 }}>
                            <Typography variant="h6" gutterBottom>
                                Processing Import...
                            </Typography>
                            <LinearProgress 
                                variant="determinate" 
                                value={importProgress}
                                style={{
                                    marginBottom: "20px",
                                    height: "14px",
                                    borderRadius: "4px"
                                }}
                            />
                            <Typography variant="body2" color="textSecondary" style={{ marginBottom: "16px" }}>
                                {Math.round(importProgress)}% Complete
                            </Typography>
                            <Box style={{ flexGrow: 1, overflow: "auto", textAlign: "left" }}>
                                {/* Show operations being processed */}
                                {importData && (
                                    <>
                                        {/* Show removals */}
                                        {importData.toRemove.map((group, index) => (
                                            <ProgressItem key={`remove-${index}`}>
                                                <Typography variant="body2">Deleting: {group.name}</Typography>
                                                <Typography color="textSecondary">
                                                    {importProgress > (index / (importData.toRemove.length + importData.toUpdate.length + importData.toAdd.length) * 100)
                                                        ? <span style={{color: "#4caf50"}}>✓ Completed</span>
                                                        : "Processing..."}
                                                </Typography>
                                            </ProgressItem>
                                        ))}
                                        {/* Show updates */}
                                        {importData.toUpdate.map((group, index) => {
                                            const overallIndex = importData.toRemove.length + index;
                                            const totalOps = importData.toRemove.length + importData.toUpdate.length + importData.toAdd.length;
                                            return (
                                                <ProgressItem key={`update-${index}`}>
                                                    <Typography variant="body2">Updating: {group.name}</Typography>
                                                    <Typography color="textSecondary">
                                                        {importProgress > (overallIndex / totalOps * 100)
                                                            ? <span style={{color: "#4caf50"}}>✓ Completed</span>
                                                            : "Processing..."}
                                                    </Typography>
                                                </ProgressItem>
                                            );
                                        })}
                                        {/* Show additions */}
                                        {importData.toAdd.map((group, index) => {
                                            const overallIndex = importData.toRemove.length + importData.toUpdate.length + index;
                                            const totalOps = importData.toRemove.length + importData.toUpdate.length + importData.toAdd.length;
                                            return (
                                                <ProgressItem key={`add-${index}`}>
                                                    <Typography variant="body2">Creating: {group.name}</Typography>
                                                    <Typography color="textSecondary">
                                                        {importProgress > (overallIndex / totalOps * 100)
                                                            ? <span style={{color: "#4caf50"}}>✓ Completed</span>
                                                            : "Processing..."}
                                                    </Typography>
                                                </ProgressItem>
                                            );
                                        })}
                                    </>
                                )}
                            </Box>
                        </ProgressContainer>)
                    )}
                </DialogContent>
                <DialogActions>
                    {importResults ? (
                        /* Close button for results */
                        (<Button
                            onClick={() => {
                                setImportPreviewOpen(false);
                                setImportResults(null);
                                setImportData(null);
                                setImportDetailedPreview([]);
                                if (importFileInput) {
                                    importFileInput.value = null;
                                }
                            }}
                            variant="contained"
                            color="primary"
                        >Close
                                                    </Button>)
                    ) : (
                        /* Cancel/Import buttons for preview and processing */
                        (<>
                            <Button
                                variant="text"
                                onClick={() => {
                                    setImportPreviewOpen(false);
                                    setImportData(null);
                                    setImportDetailedPreview([]);
                                    setImportProcessing(false);
                                    if (importFileInput) {
                                        importFileInput.value = null;
                                    }
                                }}
                                disabled={importProcessing}
                            >
                                Cancel
                            </Button>
                            {!importProcessing && (
                                <Button
                                    onClick={async () => {
                                        setImportProcessing(true);
                                        await processImport();
                                        setImportProcessing(false);
                                    }}
                                    variant="contained"
                                    color="primary"
                                    disabled={!importData || (
                                        (!importData.toAdd || importData.toAdd.length === 0) &&
                                        (!importData.toUpdate || importData.toUpdate.length === 0) &&
                                        (!importData.toRemove || importData.toRemove.length === 0)
                                    )}
                                >
                                    Import
                                </Button>
                            )}
                        </>)
                    )}
                </DialogActions>
            </PreviewDialog>
            {/* Import Instructions Dialog */}
            <Dialog
                open={importInstructionsOpen}
                onClose={() => setImportInstructionsOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Import Supplier Groups
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={3}>
                        {/* Template Download Section */}
                        <Grid item size={12}>
                            <Paper variant="outlined" style={{padding: "16px"}}>
                                <Typography variant="h6" gutterBottom>
                                    Step 1: Download Template
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{
                                    marginBottom: "16px"
                                }}>
                                    Download the CSV template to ensure your file is formatted correctly.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<ExportIcon style={{height: "18px", width: "18px"}} />}
                                    onClick={() => {
                                        const templateContent = "Group Name,Supplier IDs,Supplier Names\nExample Group 1,\"123;456;789\",\"Supplier A;Supplier B;Supplier C\"\nExample Group 2,\"111;222\",\"Supplier D;Supplier E\"";
                                        const blob = new Blob([templateContent], { type: "text/csv" });
                                        (FileSaver as any).saveAs(blob, "supplier_groups_template.csv");
                                    }}
                                >
                                    Download Template
                                </Button>
                            </Paper>
                        </Grid>
                        {/* Field Descriptions Section */}
                        <Grid item size={12}>
                            <Paper variant="outlined" style={{padding: "16px"}}>
                                <Typography variant="h6" gutterBottom>
                                    Step 2: Understand Fields
                                </Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><strong>Field Name</strong></TableCell>
                                                <TableCell><strong>Description</strong></TableCell>
                                                <TableCell><strong>Required</strong></TableCell>
                                                <TableCell><strong>Example</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>Group Name</TableCell>
                                                <TableCell>A unique name for your group</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label="Required" color="primary" />
                                                </TableCell>
                                                <TableCell><code>"West Coast Suppliers"</code></TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Supplier IDs</TableCell>
                                                <TableCell>
                                                    Logicbroker Account # separated by semicolons. At least one Account # is required, can be found in the{' '}
                                                    <Link href="/profile/account-information" target="_blank" style={{color: '#1976d2', textDecoration: 'underline'}}>
                                                        Account Info page
                                                    </Link>.
                                                </TableCell>
                                                <TableCell>
                                                    <Chip size="small" label="Required" color="primary" />
                                                </TableCell>
                                                <TableCell><code>"123;456;789"</code></TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>Supplier Names</TableCell>
                                                <TableCell>Supplier names separated by semicolons (optional - for reference only)</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label="Optional" variant="outlined" />
                                                </TableCell>
                                                <TableCell><code>"ABC Corp;XYZ Inc"</code></TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>
                        {/* Import Rules Section */}
                        <Grid item size={12}>
                            <Paper variant="outlined" style={{padding: "16px"}}>
                                <Typography variant="h6" gutterBottom>
                                    Step 3: Import Rules
                                </Typography>
                                <Typography variant="body2" sx={{
                                    marginBottom: "16px"
                                }}>
                                    <strong>How the import works:</strong>
                                </Typography>
                                <ul style={{marginLeft: "20px"}}>
                                    <li><Typography variant="body2">If a group name doesn't exist, it will be <strong>created</strong></Typography></li>
                                    <li><Typography variant="body2">If a group name exists with different suppliers, it will be <strong>updated</strong></Typography></li>
                                    <li><Typography variant="body2">If a group name exists with empty supplier IDs, it will be <strong>deleted</strong></Typography></li>
                                    <li><Typography variant="body2">Groups not mentioned in the file will remain unchanged</Typography></li>
                                </ul>
                                <Alert severity="info" style={{marginTop: "12px"}}>
                                    Use quotes around fields containing commas. Multiple supplier IDs should be separated by semicolons (;).
                                </Alert>
                            </Paper>
                        </Grid>
                        {/* Upload Section */}
                        <Grid item size={12}>
                            <Paper variant="outlined" style={{padding: "16px"}}>
                                <Typography variant="h6" gutterBottom>
                                    Step 4: Select Your File
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{
                                    marginBottom: "16px"
                                }}>
                                    Drag and drop your CSV file here, or click to browse.
                                </Typography>
                                <DropZone
                                    accept=".csv"
                                    style={{
                                        width: '100%', 
                                        height: '10em', 
                                        border: "1px dashed #93AAFD", 
                                        borderRadius: "4px"
                                    }}
                                    onDrop={(acceptedFiles) => {
                                        if (acceptedFiles && acceptedFiles.length > 0) {
                                            setImportInstructionsOpen(false);
                                            const event = {
                                                target: {
                                                    files: acceptedFiles,
                                                    value: null
                                                }
                                            };
                                            importGroups(event);
                                        }
                                    }}
                                >
                                    {({isDragAccept, isDragReject, acceptedFiles, rejectedFiles}) => {
                                        if (isDragAccept || isDragReject) {
                                            return "Drop here to upload this file.";
                                        }
                                        return 'Drop a CSV file here or click to choose a file.';
                                    }}
                                </DropZone>
                            </Paper>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button variant="text" onClick={() => setImportInstructionsOpen(false)}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </RootGrid>
    );
}